import React, { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { allUiStatuses, getFileStatus, getStatusEmoji, getStatusText, UiStatus } from 'domain/FileStatus'
import {
  parseLocationSearchAsQueryParameters,
  toQueryParamUrlString,
  updateQueryParamValue,
} from 'lib/QueryParamUtility'
import { useAutoRefresh, useRemoteData } from 'lib/RemoteData'
import { SortableHeader, useTableSorting } from 'components/assortment/TableColumnSort'
import { FileType, fileTypeDisplayName, InstallationOverview } from 'domain/DTO'
import { SelectedCountryContext } from 'domain/SelectedCountryContext'
import { SRD } from 'srd'
import { getPageData, PagedTableControls } from '@tomra/react-table'
import { API_HOST } from '../../lib'
import { hasViewAssortmentPermission, hasViewInstallationPermission } from '../../lib/Permission'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { RefreshCheckBox } from '../../components/RefreshCheckBox'
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown'
import { InstallationOverviewRow } from './components/InstallationOverviewRow'

type InstallationFilterParams = {
  textSearch: string
  commonIdFilter: string
  fileStatusFilter: string
  assortmentTypeFilter: string[]
}

export const filterInstallation = (io: InstallationOverview, filters: InstallationFilterParams): boolean => {
  const { textSearch, commonIdFilter, fileStatusFilter, assortmentTypeFilter } = filters

  const concatenatedTerms = `${io.serialNumber}:${io.chainName ?? ''}:${io.subChainName ?? ''}:${io.locationName}:${io.country}:${io.region ?? ''}`

  const enabledAssortmentStatuses = [
    { type: FileType.SHAPE, status: io.shapeStatus },
    { type: FileType.BARCODE, status: io.barcodeStatus },
    { type: FileType.CRATE, status: io.crateStatus },
    { type: FileType.LOCAL_CRATE, status: io.localCrateStatus },
    { type: FileType.ARTIKELDATEN, status: io.artikeldatenStatus },
  ].filter((item) => item.status.enabledForCommonAssortment)

  const hasEnabledStatuses = enabledAssortmentStatuses.length > 0
  const matchesTextSearch = concatenatedTerms.toLowerCase().includes(textSearch.toLowerCase())

  if (!matchesTextSearch) {
    return false
  }

  // Combined assortment type and file status filter
  if (assortmentTypeFilter.length > 0) {
    const getStatusForType = (type: string) => {
      switch (type) {
        case FileType.SHAPE:
          return io.shapeStatus
        case FileType.BARCODE:
          return io.barcodeStatus
        case FileType.CRATE:
          return io.crateStatus
        case FileType.LOCAL_CRATE:
          return io.localCrateStatus
        case FileType.ARTIKELDATEN:
          return io.artikeldatenStatus
        default:
          return null
      }
    }

    const allTypesMatch = assortmentTypeFilter.every((type) => {
      const status = getStatusForType(type)
      if (!status || !status.enabledForCommonAssortment) {
        return false
      }
      // If file status filter is set, also check that this type has the matching status
      if (fileStatusFilter !== '') {
        const fileStatus = getFileStatus(status, type as FileType)
        return fileStatus.uiStatus === fileStatusFilter
      }
      return true
    })
    if (!allTypesMatch) {
      return false
    }
  } else if (fileStatusFilter !== '') {
    // No assortment type filter, but file status filter is set - check any enabled assortment
    if (!hasEnabledStatuses) {
      return false
    }
    const fileStatuses = enabledAssortmentStatuses.map(({ type, status }) => getFileStatus(status, type))
    if (!fileStatuses.some((s) => s.uiStatus === fileStatusFilter)) {
      return false
    }
  }

  if (commonIdFilter === 'NotAssigned') {
    return !io.commonAssortmentId
  }
  if (commonIdFilter !== '') {
    return io.commonAssortmentId === commonIdFilter
  }

  return true
}

export const InstallationsOverview = () => {
  const [textSearch, setValue] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPerPage, setRecordsPerPage] = useState(100)
  const navigate = useNavigate()
  const location = useLocation()
  const queryParameters = parseLocationSearchAsQueryParameters(location.search)
  const { selectedCountry } = useContext(SelectedCountryContext)
  const { data: contextRemoteData, refresh } = useRemoteData<InstallationOverview[]>(
    `${API_HOST}/bff/installations?country=${selectedCountry}`,
  )

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(false)

  const refreshIntervalInSeconds = 60
  useAutoRefresh(refresh, refreshIntervalInSeconds * 1000, autoRefreshEnabled)

  const commonIdFilter = queryParameters.find(([key, _]) => key === 'commonId')?.[1] ?? ''
  const fileStatusFilter = queryParameters.find(([key, _]) => key === 'fileStatus')?.[1] ?? ''
  const assortmentTypeFilter =
    queryParameters
      .find(([key]) => key === 'assortmentType')?.[1]
      ?.split(',')
      .filter(Boolean) ?? []
  const replaceQueryParamAndPushToHistory = (key: string, newValue: string) => {
    setCurrentPage(1)
    const urlToPush = location.pathname + toQueryParamUrlString(updateQueryParamValue(queryParameters, key, newValue))
    navigate(urlToPush)
  }

  const sorting = useTableSorting<InstallationOverview>(
    {
      Serial: (io: InstallationOverview) => io.serialNumber,
      Common: (io: InstallationOverview) => io.commonAssortmentName ?? '',
      'Chain / Sub Chain': (io: InstallationOverview) => (io.chainName ?? io.subChainName ?? '').trim(),
      'Location (Country Code, Region)': (io: InstallationOverview) =>
        `${io.locationName} [${io.country}${io.region ? ', ' + io.region : ''}]`.trim(),
    },
    'Serial',
  )

  return SRD.match(
    {
      notAsked: () => <div>Empty</div>,
      loading: () => <LoadingSpinner />,
      failure: (err) => (
        <div className="card">
          <div className="centerAbsolute">
            <div className="alert alert-danger">
              Uh-oh.. Failed to load Installations
              <div>{err.body?.title}</div>
            </div>
          </div>
        </div>
      ),
      success: (value: InstallationOverview[]) => {
        const installationsForSelectedCountry = value.filter((i: InstallationOverview) => i.country === selectedCountry)

        const filters = { textSearch, commonIdFilter, fileStatusFilter, assortmentTypeFilter }
        const dataSelection = installationsForSelectedCountry.filter((io) => filterInstallation(io, filters))

        const filteredData = sorting.sort(dataSelection)
        const csvHeader = 'Serial;Chain;Subchain;Location\n'
        const filteredDataCsv =
          csvHeader +
          filteredData
            .map((i: InstallationOverview) => `${i.serialNumber};${i.chainName};${i.subChainName};${i.locationName}`)
            .join('\n')
        return (
          (hasViewAssortmentPermission() || hasViewInstallationPermission()) && (
            <div className="card">
              <div className="flex justify-between p-md">
                <div className="inline-flex">
                  <input
                    type="text"
                    className="textfield w-auto"
                    data-testid="search-installation"
                    onChange={(e) => {
                      setValue(e.target.value)
                      setCurrentPage(1)
                    }}
                  />
                  <select
                    className="select w-auto ml-md"
                    onChange={(e) => replaceQueryParamAndPushToHistory('commonId', e.target.value)}
                    value={commonIdFilter ?? ''}
                  >
                    <option key={0} value="">
                      Select Common
                    </option>
                    <option key={1} value="NotAssigned">{`Not Assigned [${
                      installationsForSelectedCountry.filter((i: InstallationOverview) => !i.commonAssortmentId).length
                    }]`}</option>
                    {(
                      Array.from(
                        new Map(
                          value
                            .filter(
                              (io: InstallationOverview) => io.country === selectedCountry && io.commonAssortmentId,
                            )
                            .map((io: InstallationOverview) => [io.commonAssortmentId, io]),
                        ).values(),
                      ) as InstallationOverview[]
                    )
                      .map((installationOverview: InstallationOverview) => {
                        const installationsWithCommonAssortment = installationsForSelectedCountry.filter(
                          (io: InstallationOverview) =>
                            io.commonAssortmentId === installationOverview.commonAssortmentId,
                        ).length
                        if (installationsWithCommonAssortment > 0) {
                          return (
                            <option
                              key={installationOverview.commonAssortmentId}
                              value={installationOverview.commonAssortmentId!}
                            >
                              {`${installationOverview.commonAssortmentName} [ ${installationsWithCommonAssortment} ]`}
                            </option>
                          )
                        }
                        return null
                      })
                      .filter(Boolean)}
                  </select>
                </div>
                <div className="inline-flex float-right items-center space-x-2">
                  <div className="inline">
                    <RefreshCheckBox
                      checked={autoRefreshEnabled ?? false}
                      onToggle={setAutoRefreshEnabled}
                      title={`Refresh every ${refreshIntervalInSeconds} seconds`}
                    />
                  </div>
                  <MultiSelectDropdown
                    options={[
                      { value: FileType.SHAPE, label: fileTypeDisplayName(FileType.SHAPE) },
                      { value: FileType.BARCODE, label: fileTypeDisplayName(FileType.BARCODE) },
                      { value: FileType.CRATE, label: fileTypeDisplayName(FileType.CRATE) },
                      { value: FileType.LOCAL_CRATE, label: fileTypeDisplayName(FileType.LOCAL_CRATE) },
                      ...(selectedCountry === 'DE'
                        ? [{ value: FileType.ARTIKELDATEN, label: fileTypeDisplayName(FileType.ARTIKELDATEN) }]
                        : []),
                    ]}
                    selected={assortmentTypeFilter}
                    onChange={(selected) => replaceQueryParamAndPushToHistory('assortmentType', selected.join(','))}
                    placeholder="Assortment Types"
                  />
                  <select
                    className="select w-auto border-grey"
                    onChange={(e) => replaceQueryParamAndPushToHistory('fileStatus', e.target.value)}
                    value={fileStatusFilter}
                  >
                    <option key={0} value="">
                      All Assortment Statuses
                    </option>
                    {allUiStatuses.map((uiStatus) => (
                      <option key={uiStatus} value={uiStatus}>
                        {getStatusEmoji(uiStatus as UiStatus) + ' ' + getStatusText(uiStatus as UiStatus)}
                      </option>
                    ))}
                  </select>
                  <a
                    className="btn btn-primary-dark ml-md py-sm"
                    download="installations.csv"
                    href={'data:text/csv;charset=utf-8,' + encodeURIComponent(filteredDataCsv)}
                  >
                    Export
                  </a>
                </div>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <SortableHeader name="Serial" currentSort={sorting.currentSort} minWidth="8em" />
                    <SortableHeader name="Common" currentSort={sorting.currentSort} minWidth="14em" />
                    <th style={{ minWidth: '16em' }}>Assortment Status</th>
                    <SortableHeader name="Chain / Sub Chain" currentSort={sorting.currentSort} minWidth="10em" />
                    <SortableHeader
                      name="Location (Country Code, Region)"
                      currentSort={sorting.currentSort}
                      minWidth="40em"
                    />
                  </tr>
                </thead>
                <tbody>
                  {getPageData(filteredData, currentPage, recordsPerPage).map((inst: InstallationOverview) => {
                    return <InstallationOverviewRow installation={inst} key={inst.installationId} />
                  })}
                </tbody>
              </table>
              <PagedTableControls
                currentPage={currentPage}
                numOfItems={filteredData.length}
                recordsPerPage={recordsPerPage}
                onPageUpdate={setCurrentPage}
                recordsPerPageOptions={[10, 20, 50, 100]}
                onRecordsPerPageChange={(recordsPerPage: number) => {
                  setRecordsPerPage(recordsPerPage)
                  setCurrentPage(1)
                }}
              />
            </div>
          )
        )
      },
    },
    contextRemoteData,
  )
}
